import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight, MapPin, Loader2, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MEDIA } from "@/lib/media";

export default function Outfitters() {
  const navigate = useNavigate();
  const [listings, setListings] = useState(null);

  useEffect(() => { api.huntListings().then((d) => setListings(Array.isArray(d) ? d : (d.listings || []))).catch(() => setListings([])); }, []);

  return (
    <div className="bg-paper min-h-screen" data-testid="outfitters-page">
      <SiteHeader />
      <section className="relative bg-bone overflow-hidden" data-testid="outfitters-hero">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="px-5 sm:px-10 pt-8 pb-12 lg:py-20 flex flex-col justify-center relative z-10">
            <nav className="flex items-center gap-1.5 text-xs text-charcoal/50 mb-5" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} /><span className="text-forest font-medium">Outfitters &amp; Hunts</span>
            </nav>
            <span className="hr-overline text-blaze">Outfitters &amp; Guided Hunts</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-forest mt-3">Find the hunt you&apos;ve been looking for.</h1>
            <p className="text-base text-charcoal/70 mt-5 max-w-md">Discover trusted outfitters and guided hunts that fit your species, terrain and season — every listing from a verified HomeRange business.</p>
            <button onClick={() => navigate("/explore")} className="mt-7 inline-flex w-fit items-center gap-2 bg-blaze text-white px-6 py-3.5 font-semibold uppercase tracking-wider text-sm hover:bg-blaze-light transition-colors" data-testid="outfitters-plan">Plan by state <ArrowRight size={16} /></button>
          </div>
          <div className="relative min-h-[240px] lg:min-h-0">
            <img src={MEDIA.exploreOutfitters} alt="Backcountry hunting camp at dusk" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/10 to-transparent hidden lg:block" />
          </div>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-14">
        <h2 className="font-serif text-3xl sm:text-4xl text-forest tracking-tight">Featured hunts</h2>
        {listings === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : listings.length === 0 ? (
          <div className="mt-6 text-center py-16 border border-dashed border-saddle/30" data-testid="outfitters-empty">
            <Compass size={32} className="mx-auto text-saddle/60" />
            <p className="font-serif text-2xl text-forest mt-4">Guided hunts are coming soon.</p>
            <p className="text-charcoal/60 mt-2 max-w-md mx-auto">We only list verified outfitters. In the meantime, plan your own hunt by state.</p>
            <button onClick={() => navigate("/explore")} className="mt-5 bg-forest text-bone px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze transition-colors" data-testid="outfitters-explore-cta">Explore states</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="outfitters-grid">
            {listings.map((l, i) => (
              <motion.button key={l.id} onClick={() => navigate(`/hunt/${l.id}`)}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.03 }}
                className="group relative h-[320px] overflow-hidden text-left bg-charcoal flex flex-col justify-end" data-testid={`outfitter-card-${l.id}`}>
                <img src={l.image_url || MEDIA.exploreOutfitters} alt={l.title || "Guided hunt"} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/40 to-transparent" />
                <div className="relative p-5">
                  <span className="hr-overline text-blaze text-[10px]">{[l.species, l.state || l.jurisdiction].filter(Boolean).join(" · ")}</span>
                  <h3 className="font-serif text-2xl text-bone leading-tight mt-1">{l.title || `${l.species} Hunt`}</h3>
                  {l.outfitter_name && <p className="text-bone/60 text-sm mt-1 flex items-center gap-1"><MapPin size={12} /> {l.outfitter_name}</p>}
                  <span className="inline-flex items-center gap-1.5 text-blaze text-sm font-semibold mt-3">View hunt <ArrowRight size={14} /></span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
